import clsx from 'clsx'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { match } from 'ts-pattern'

import Header from '@/components-latest/layout/Header'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import ErrorState from '@/components-latest/shared/ErrorState'
import BillingCycleDetails from '@/components-latest/ui/BillingPage/BillingCycleDetails'
import BillingCycleFilter from '@/components-latest/ui/BillingPage/BillingCycleFilter'
import BillingCyclesList from '@/components-latest/ui/BillingPage/BillingCyclesList'
import EmptyBillingPage from '@/components-latest/ui/BillingPage/EmptyBillingPage'
import WalletCard from '@/components-latest/ui/BillingPage/WalletCard'
import {
  DEFAULT_WALLET_TRANSACTIONS_PAGE_SIZE,
  WalletTransactionsPageSize
} from '@/components-latest/ui/BillingPage/WalletTransactions/constants'
import type { WalletTransaction } from '@/components-latest/ui/BillingPage/WalletTransactions/types'
import WalletTransactions from '@/components-latest/ui/BillingPage/WalletTransactions/WalletTransactions'
import { selectToken } from '@/ducks/auth/auth.selectors'
import { useAccessControl } from '@/ducks/auth/auth.permissions'
import {
  useLazyFetchBillingCyclesQuery,
  useLazyFetchStoreWalletFundingMethodsQuery,
  useLazyFetchWalletDetailsQuery,
  useLazyFetchWalletTransactionsQuery
} from '@/ducks/billing/api/billing.api'
import {
  buildFetchWalletTransactionsParams,
  type WalletTransactionsDateRangePreset
} from '@/ducks/billing/billing.wallet-transactions.filters.helpers'
import {
  canRequestStoreWalletPayout,
  selectWithdrawPaymentMethod,
  WALLET_PAYOUT_LD_FLAG_KEY
} from '@/ducks/billing/billing.wallet-funding'
import { sumWalletLedgerPending } from '@/ducks/billing/billing.wallet-details'
import {
  linkWalletFundingAttemptToTransaction,
  rememberWalletFundingAttemptLink,
  resolveFundingAttemptIdForWalletTransaction
} from '@/ducks/billing/billing.wallet-funding-links'
import {
  IBillingCycle,
  IGetBillingCyclesResponse,
  IWalletTransactionApiItem
} from '@/ducks/billing/billing.types'
import { resolveWalletTransactionClient } from '@/ducks/billing/billing.wallet-transactions'
import {
  selectCurrencyCode,
  selectPhoneCode,
  selectStoreCashCoStatus,
  selectStoreId,
  selectStoreLedgerWalletEnabled
} from '@/ducks/store/selectors/store.selectors'

import Styles from './BillingPage.module.scss'

const WALLET_FLAG_KEY_KEBAB = 'lmd-store-enable-restaurant-wallet-web'
const WALLET_CURRENCY_CODE = 'DA'

const formatWalletDateTime = (
  value: string,
  locale: string,
  todayLabel: string
) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()

  const timePart = date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit'
  })

  if (isToday) {
    return `${todayLabel}, ${timePart}`
  }

  const datePart = date.toLocaleDateString(locale, {
    day: '2-digit',
    month: 'short'
  })
  return `${datePart}, ${timePart}`
}

const mapWalletStatus = (
  status: string | undefined
): WalletTransaction['status'] => {
  const normalizedStatus = String(status || '').toUpperCase()
  if (normalizedStatus === 'CONFIRMED' || normalizedStatus === 'DONE') {
    return 'done'
  }
  if (normalizedStatus === 'REFUND' || normalizedStatus === 'REFUNDED') {
    return 'refund'
  }
  if (normalizedStatus === 'FAILED') {
    return 'failed'
  }
  if (
    normalizedStatus === 'CANCELLED' ||
    normalizedStatus === 'CANCELED'
  ) {
    return 'canceled'
  }
  return 'pending'
}

const mapWalletType = (transactionType: string | undefined) => {
  const normalizedType = String(transactionType || '').toUpperCase()
  if (normalizedType.includes('TOP_UP')) return 'Top up'
  if (normalizedType.includes('CASH_OUT')) return 'Cash out'
  if (normalizedType.includes('ORDER')) return 'Order'
  return 'Order'
}

const BillingPage = () => {
  const { t, i18n } = useTranslation()

  const storeId = useSelector(selectStoreId)
  const isStoreUsingCashCo = useSelector(selectStoreCashCoStatus)
  const isStoreLedgerWalletEnabled = useSelector(selectStoreLedgerWalletEnabled)
  const canAccessIncome =
    Boolean(isStoreUsingCashCo) || Boolean(isStoreLedgerWalletEnabled)
  const showBillingCycles = Boolean(isStoreUsingCashCo)
  const authToken = useSelector(selectToken)

  const flags = useFlags()
  const { lmd_store_webapp_FR_Billing } = flags
  const isRestaurantWalletEnabled = Boolean(flags?.[WALLET_FLAG_KEY_KEBAB])
  const isWalletPayoutEnabled = Boolean(flags?.[WALLET_PAYOUT_LD_FLAG_KEY])
  const { canRequestWalletPayout: isStoreOwnerForPayout } = useAccessControl()

  const phoneCode = useSelector(selectPhoneCode)
  const currencyCode = useSelector(selectCurrencyCode) || ''

  const [
    getBillingCycles,
    { isFetching, data, isError: isBillingCyclesError }
  ] = useLazyFetchBillingCyclesQuery()
  const [
    getWalletTransactions,
    {
      data: walletTransactionsResponse,
      isFetching: isWalletTransactionsFetching
    }
  ] = useLazyFetchWalletTransactionsQuery()
  const [getWalletDetails, { data: walletDetailsResponse }] =
    useLazyFetchWalletDetailsQuery()
  const [
    getFundingMethods,
    {
      data: fundingMethodsResponse,
      isFetching: isFundingMethodsFetching,
      isError: isFundingMethodsError,
      error: fundingMethodsError
    }
  ] = useLazyFetchStoreWalletFundingMethodsQuery()

  const loadBillingCycles = useCallback(() => {
    if (!storeId || !showBillingCycles) return
    getBillingCycles({ storeId })
  }, [storeId, getBillingCycles, showBillingCycles])

  useEffect(() => {
    loadBillingCycles()
  }, [loadBillingCycles])

  useEffect(() => {
    if (!storeId || !canAccessIncome || !isRestaurantWalletEnabled) return
    getWalletDetails({ token: authToken || undefined })
  }, [
    storeId,
    authToken,
    getWalletDetails,
    canAccessIncome,
    isRestaurantWalletEnabled
  ])

  const [activeCycle, setActiveCycle] = useState<IBillingCycle | null>(null)
  const [activeCycleType, setActiveCycleType] = useState<
    'paid' | 'unpaid' | null
  >(null)
  const [isWalletTransactionsOpen, setIsWalletTransactionsOpen] =
    useState(false)
  const [walletTxPage, setWalletTxPage] = useState(0)
  const [walletTxLimit, setWalletTxLimit] = useState<WalletTransactionsPageSize>(
    DEFAULT_WALLET_TRANSACTIONS_PAGE_SIZE
  )
  const [walletBrandFilter, setWalletBrandFilter] = useState<
    'kool' | 'yassir' | undefined
  >(undefined)
  const [walletDateRange, setWalletDateRange] = useState<
    WalletTransactionsDateRangePreset | undefined
  >(undefined)
  const [walletDateFrom, setWalletDateFrom] = useState<string | undefined>(
    undefined
  )
  const [walletDateTo, setWalletDateTo] = useState<string | undefined>(
    undefined
  )

  const walletTxOffset = walletTxPage * walletTxLimit

  const clearWalletTransactionFilters = useCallback(() => {
    setWalletBrandFilter(undefined)
    setWalletDateRange(undefined)
    setWalletDateFrom(undefined)
    setWalletDateTo(undefined)
    setWalletTxPage(0)
  }, [])

  const walletTransactionsFetchParams = useMemo(
    () =>
      buildFetchWalletTransactionsParams({
        limit: walletTxLimit,
        offset: walletTxOffset,
        filters: {
          ...(walletBrandFilter ? { brand: walletBrandFilter } : {}),
          ...(walletDateRange ? { dateRange: walletDateRange } : {}),
          ...(walletDateFrom && walletDateTo
            ? { dateFrom: walletDateFrom, dateTo: walletDateTo }
            : {})
        }
      }),
    [
      walletBrandFilter,
      walletDateFrom,
      walletDateRange,
      walletDateTo,
      walletTxLimit,
      walletTxOffset
    ]
  )
  const [cycleFilter, setCycleFilter] = useState<
    'paid' | 'unpaid' | 'unreceived'
  >('unpaid')

  const isPaidStatus = (paidStatus: unknown) => {
    const normalizedStatus = String(paidStatus).toLowerCase()
    return (
      normalizedStatus === '1' ||
      normalizedStatus === 'paid' ||
      normalizedStatus === 'true'
    )
  }

  const normalizedCycles = useMemo(() => {
    if (!data) {
      return {
        paid: [] as IBillingCycle[],
        unpaid: [] as IBillingCycle[],
        unreceived: [] as IBillingCycle[]
      }
    }

    const { paid_billing = [], unpaid_billing = [] } =
      data as IGetBillingCyclesResponse
    const mergedCycles = [...unpaid_billing, ...paid_billing]

    const uniqueByCycle = Array.from(
      new Map(mergedCycles.map(cycle => [cycle.billing_cycle, cycle])).values()
    )

    return {
      paid: uniqueByCycle.filter(cycle => isPaidStatus(cycle.paid_status)),
      unpaid: uniqueByCycle.filter(cycle => !isPaidStatus(cycle.paid_status)),
      unreceived: uniqueByCycle.filter(
        cycle =>
          !isPaidStatus(cycle.paid_status) &&
          Number(cycle?.billings?.final_admin_earnings || 0) > 0
      )
    }
  }, [data])

  const openCycleDetailsCreator = (
    cycle: IBillingCycle | null,
    type: 'paid' | 'unpaid'
  ) => {
    if (!cycle || !type) {
      setActiveCycle(null)
      setActiveCycleType(null)
      return
    }
    setActiveCycle(cycle)
    setActiveCycleType(type)
  }

  useEffect(() => {
    if (!isWalletTransactionsOpen) return
    getWalletTransactions(walletTransactionsFetchParams)
  }, [
    isWalletTransactionsOpen,
    walletTransactionsFetchParams,
    getWalletTransactions
  ])

  useEffect(() => {
    if (!isWalletTransactionsOpen || !isRestaurantWalletEnabled) return
    getWalletDetails({ token: authToken || undefined })
  }, [
    isWalletTransactionsOpen,
    isRestaurantWalletEnabled,
    authToken,
    getWalletDetails
  ])

  useEffect(() => {
    if (!isWalletTransactionsOpen || !isRestaurantWalletEnabled) return
    getFundingMethods()
  }, [getFundingMethods, isRestaurantWalletEnabled, isWalletTransactionsOpen])

  const refreshWalletData = () => {
    getWalletDetails({ token: authToken || undefined })
    getWalletTransactions(walletTransactionsFetchParams)
    if (walletDetailsResponse?.walletSource === 'store-wallet') {
      getFundingMethods()
    }
  }

  const handlePayoutSuccess = (result?: {
    fundingAttemptId: string
    amount: number
    currency: string
  }) => {
    if (result?.fundingAttemptId) {
      rememberWalletFundingAttemptLink({
        fundingAttemptId: result.fundingAttemptId,
        amount: result.amount,
        currency: result.currency
      })
    }
    refreshWalletData()
  }

  useEffect(() => {
    if (!data) return
    const {
      unpaid: unpaidBilling,
      paid: paidBilling,
      unreceived: unreceivedBilling
    } = normalizedCycles
    if (
      cycleFilter === 'unpaid' &&
      unpaidBilling.length === 0 &&
      paidBilling.length
    ) {
      setCycleFilter('paid')
      return
    }

    if (
      cycleFilter === 'paid' &&
      paidBilling.length === 0 &&
      unpaidBilling.length
    ) {
      setCycleFilter('unpaid')
    }

    if (cycleFilter === 'unreceived' && unreceivedBilling.length === 0) {
      if (unpaidBilling.length) {
        setCycleFilter('unpaid')
        return
      }
      if (paidBilling.length) {
        setCycleFilter('paid')
      }
    }
  }, [data, cycleFilter, normalizedCycles])

  const storeWalletAmount = useMemo(() => {
    if (!data) return 0

    const sortedCycles = [
      ...normalizedCycles.unpaid,
      ...normalizedCycles.paid
    ].sort(
      (cycleA, cycleB) =>
        (cycleB?.details?.end_time || 0) - (cycleA?.details?.end_time || 0)
    )

    return sortedCycles[0]?.billings?.last_billings_credit || 0
  }, [data, normalizedCycles])

  const walletAmountFromApi = walletDetailsResponse?.balance
  const walletCurrencyFromApi = walletDetailsResponse?.currency
  const resolvedWalletAmount =
    typeof walletAmountFromApi === 'number' &&
    Number.isFinite(walletAmountFromApi)
      ? walletAmountFromApi
      : storeWalletAmount
  const resolvedWalletCurrency =
    walletCurrencyFromApi || currencyCode || WALLET_CURRENCY_CODE

  const withdrawableBalance =
    typeof walletAmountFromApi === 'number' && Number.isFinite(walletAmountFromApi)
      ? walletAmountFromApi
      : 0

  const pendingBalance = sumWalletLedgerPending(
    walletDetailsResponse?.ledgerPending
  )
  const showPendingBalance =
    walletDetailsResponse?.walletSource === 'store-wallet'

  // Prefer login enroll for wallet-only; fall back to BFF enrolled for Cash CO + wallet.
  const isStoreLedgerWalletEnrolled = isStoreLedgerWalletEnabled
    ? true
    : walletDetailsResponse?.walletSource !== 'store-wallet' ||
      walletDetailsResponse?.enrolled !== false
  const showWalletCard =
    isRestaurantWalletEnabled && isStoreLedgerWalletEnrolled

  const hasWithdrawPaymentMethod =
    !isFundingMethodsFetching &&
    !isFundingMethodsError &&
    Boolean(selectWithdrawPaymentMethod(fundingMethodsResponse))

  const canRequestPayout = useMemo(
    () =>
      canRequestStoreWalletPayout({
        walletSource: walletDetailsResponse?.walletSource,
        balance: withdrawableBalance,
        isWalletFeatureEnabled: isRestaurantWalletEnabled,
        isWalletPayoutEnabled,
        hasWithdrawPaymentMethod,
        isStoreOwner: isStoreOwnerForPayout
      }),
    [
      walletDetailsResponse?.walletSource,
      withdrawableBalance,
      isRestaurantWalletEnabled,
      isWalletPayoutEnabled,
      hasWithdrawPaymentMethod,
      isStoreOwnerForPayout
    ]
  )

  const requestPayoutDisabledReason = useMemo(() => {
    if (canRequestPayout) return null
    if (!isWalletPayoutEnabled) return 'country' as const
    return 'generic' as const
  }, [canRequestPayout, isWalletPayoutEnabled])

  const walletTransactions = useMemo<WalletTransaction[]>(() => {
    const items = walletTransactionsResponse?.data || []
    const currentLocale = i18n.resolvedLanguage || i18n.language || 'en'
    const todayLabel = t('billing.wallet.today')

    return items.map((transaction: IWalletTransactionApiItem) => {
      const rawAmount = transaction.amount_with_fee ?? transaction.amount ?? 0
      const signedAmount =
        String(transaction.entry_type).toUpperCase() === 'DEBIT'
          ? -Math.abs(rawAmount)
          : Math.abs(rawAmount)

      const { clientName, clientAvatar } =
        resolveWalletTransactionClient(transaction)

      return {
        id: transaction.transaction_id,
        type: mapWalletType(transaction.transaction_type),
        dateTime: formatWalletDateTime(
          transaction.transaction_date_time,
          currentLocale,
          todayLabel
        ),
        clientName,
        clientAvatar,
        orderId: transaction.order_id || '-',
        status: mapWalletStatus(transaction.status),
        cashOutDisplayStatus: transaction.cash_out_display_status,
        amount: signedAmount,
        currency: transaction.currency || WALLET_CURRENCY_CODE,
        fundingAttemptId: resolveFundingAttemptIdForWalletTransaction({
          transactionId: transaction.transaction_id,
          type: mapWalletType(transaction.transaction_type),
          status: mapWalletStatus(transaction.status),
          amount: signedAmount,
          fundingAttemptId: transaction.funding_attempt_id
        }),
        brand: (transaction.brand as 'kool' | 'yassir') ?? null
      }
    })
  }, [i18n.language, i18n.resolvedLanguage, t, walletTransactionsResponse])

  useEffect(() => {
    walletTransactions.forEach(transaction => {
      if (!transaction.fundingAttemptId) return
      linkWalletFundingAttemptToTransaction(
        transaction.fundingAttemptId,
        transaction.id
      )
    })
  }, [walletTransactions])

  if (!canAccessIncome) return null

  if (!lmd_store_webapp_FR_Billing && phoneCode === '+33') return null

  const isBillingCyclesLoading = showBillingCycles && isFetching

  return (
    <div className={Styles.page} data-testid="billing-page">
      <Header
        onGoBack={
          activeCycle
            ? () => {
                setActiveCycle(null)
              }
            : undefined
        }
        title={activeCycle ? t('billing.page-title-details') : ''}
      />
      <div className={Styles.content}>
        {match({ isFetching, data, activeCycle, isBillingCyclesLoading })
          .when(
            ({ isBillingCyclesLoading }) => isBillingCyclesLoading,
            () => (
              <div className={Styles.spinner}>
                <Spinner />
              </div>
            )
          )
          .when(
            () => showBillingCycles && isBillingCyclesError && !data,
            () => (
              <ErrorState
                data-testid="billing-error"
                onRetry={loadBillingCycles}
              />
            )
          )
          .when(
            () => showBillingCycles && !data,
            () => (
              <div className={Styles.spinner}>
                <Spinner />
              </div>
            )
          )
          .when(
            () =>
              !showWalletCard &&
              (!showBillingCycles ||
                (normalizedCycles.paid.length === 0 &&
                  normalizedCycles.unpaid.length === 0)),
            () => <EmptyBillingPage />
          )
          .when(
            ({ activeCycle }) => !activeCycle,
            () => {
              if (isWalletTransactionsOpen) {
                return (
                  <WalletTransactions
                    onBack={() => {
                      setIsWalletTransactionsOpen(false)
                      setWalletTxPage(0)
                      setWalletBrandFilter(undefined)
                      setWalletDateRange(undefined)
                      setWalletDateFrom(undefined)
                      setWalletDateTo(undefined)
                    }}
                    transactions={walletTransactions}
                    isLoading={isWalletTransactionsFetching}
                    settledBalance={withdrawableBalance || resolvedWalletAmount}
                    pendingBalance={pendingBalance}
                    showPendingBalance={showPendingBalance}
                    currencyCode={resolvedWalletCurrency}
                    page={walletTxPage}
                    limit={walletTxLimit}
                    offset={walletTxOffset}
                    total={walletTransactionsResponse?.pagination?.total}
                    onPageChange={setWalletTxPage}
                    onLimitChange={limit => {
                      setWalletTxLimit(limit)
                      setWalletTxPage(0)
                    }}
                    canRequestPayout={canRequestPayout}
                    requestPayoutDisabledReason={requestPayoutDisabledReason}
                    fundingMethods={fundingMethodsResponse}
                    isFundingMethodsLoading={isFundingMethodsFetching}
                    fundingMethodsError={
                      isFundingMethodsError ? fundingMethodsError : undefined
                    }
                    onPayoutSuccess={handlePayoutSuccess}
                    brandFilter={walletBrandFilter}
                    onBrandFilterChange={brand => {
                      setWalletBrandFilter(brand)
                      setWalletTxPage(0)
                    }}
                    dateRange={walletDateRange}
                    dateFrom={walletDateFrom}
                    dateTo={walletDateTo}
                    onDatePresetChange={preset => {
                      setWalletDateRange(preset)
                      if (preset) {
                        setWalletDateFrom(undefined)
                        setWalletDateTo(undefined)
                      }
                      setWalletTxPage(0)
                    }}
                    onCustomDateRangeChange={(dateFrom, dateTo) => {
                      setWalletDateRange(undefined)
                      setWalletDateFrom(dateFrom)
                      setWalletDateTo(dateTo)
                      setWalletTxPage(0)
                    }}
                    onClearDateFilter={() => {
                      setWalletDateRange(undefined)
                      setWalletDateFrom(undefined)
                      setWalletDateTo(undefined)
                      setWalletTxPage(0)
                    }}
                    onClearAllFilters={clearWalletTransactionFilters}
                    formatDateTime={value =>
                      formatWalletDateTime(
                        value,
                        i18n.resolvedLanguage || i18n.language || 'en',
                        t('billing.wallet.today')
                      )
                    }
                  />
                )
              }

              const {
                paid: paidBilling,
                unpaid: unpaidBilling,
                unreceived: unreceivedBilling
              } = normalizedCycles
              const hasUnpaidCycles = unpaidBilling.length > 0
              const hasPaidCycles = paidBilling.length > 0
              const hasUnreceivedCycles = unreceivedBilling.length > 0

              const cyclesToDisplay = match(cycleFilter)
                .with('unpaid', () => unpaidBilling)
                .with('paid', () => paidBilling)
                .with('unreceived', () => unreceivedBilling)
                .exhaustive()

              return (
                <ContentBlock className={Styles.billingCard}>
                  <div className={Styles.titleRow}>
                    <div className={Styles.titleContent}>
                      <Text variant="nextBody" className={Styles.cardTitle}>
                        {t('billing.page-title')}
                      </Text>
                    </div>
                  </div>
                  <div
                    className={clsx(Styles.billingSections, {
                      [Styles.billingSectionsWithoutWallet]: !showWalletCard
                    })}>
                    {showWalletCard && (
                      <WalletCard
                        amount={resolvedWalletAmount}
                        currencyCode={resolvedWalletCurrency}
                        onOpen={() => {
                          setIsWalletTransactionsOpen(true)
                        }}
                      />
                    )}
                    {showBillingCycles && (
                      <>
                        <BillingCycleFilter
                          activeFilter={cycleFilter}
                          hasPaidCycles={hasPaidCycles}
                          hasUnpaidCycles={hasUnpaidCycles}
                          hasUnreceivedCycles={hasUnreceivedCycles}
                          onFilterChange={setCycleFilter}
                          noTopPadding={!showWalletCard}
                        />
                        <BillingCyclesList
                          onDetailsClick={openCycleDetailsCreator}
                          cycles={cyclesToDisplay}
                          type={cycleFilter}
                          showTitle={false}
                        />
                      </>
                    )}
                  </div>
                </ContentBlock>
              )
            }
          )
          .when(
            ({ activeCycle }) => activeCycle,
            () => {
              return (
                <BillingCycleDetails
                  cycle={activeCycle}
                  type={activeCycleType}
                />
              )
            }
          )
          .otherwise(() => null)}
      </div>
    </div>
  )
}

export default BillingPage
